import {
  ClockCircleOutlined,
  ReloadOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Row,
  Select,
  Space,
  Statistic,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  IAreaDeliveryTimeStat,
  IAreaDeliveryTimeStats,
} from "../../../models/statistcs/respons";
import EndPoints from "../../../services/end-points";
import Table from "../../general/table-components/table";
import styles from "./style.module.scss";

interface IProps {
  stats?: IAreaDeliveryTimeStats;
  onChange: (stats: IAreaDeliveryTimeStats) => void;
}

interface IAreaDeliveryTimeRow extends IAreaDeliveryTimeStat {
  key: string;
  isOutside: boolean;
}

type SortOption =
  | "highest_average"
  | "lowest_average"
  | "highest_order_count"
  | "area_name";

type FieldErrors = Partial<Record<"startDate" | "endDate", string>>;

const DATE_FORMAT = "YYYY-MM-DD";

const extractFieldError = (error: any, field: "startDate" | "endDate") => {
  const errors = error?.response?.data?.errors;
  const value = errors?.[field];

  if (Array.isArray(value)) return value.filter(Boolean).join(", ");
  if (typeof value === "string") return value;

  return undefined;
};

const getNullableNumber = (value: number | null | undefined) =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const normalizeDatePickerValue = (value: string | string[]) =>
  Array.isArray(value) ? value[0] : value || undefined;

const AreaDeliveryTimeStats: FC<IProps> = ({ stats, onChange }) => {
  const { t, i18n } = useTranslation();
  const [startDate, setStartDate] = useState<string | undefined>(
    stats?.startDate
  );
  const [endDate, setEndDate] = useState<string | undefined>(stats?.endDate);
  const [sortOption, setSortOption] =
    useState<SortOption>("highest_average");
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const numberFormatter = useMemo(
    () => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }),
    [i18n.language]
  );

  useEffect(() => {
    setStartDate(stats?.startDate);
    setEndDate(stats?.endDate);
  }, [stats?.endDate, stats?.startDate]);

  const rows = useMemo<IAreaDeliveryTimeRow[]>(() => {
    if (!stats) return [];

    const buildRow = (
      item: IAreaDeliveryTimeStat,
      index: number,
      isOutside = false
    ): IAreaDeliveryTimeRow => ({
      ...item,
      key: isOutside ? "outside-areas" : `area-${item.areaId ?? index}`,
      isOutside,
    });

    const data = [
      ...(stats.areas || []).map((area, index) => buildRow(area, index)),
      ...(stats.outsideAreas
        ? [buildRow(stats.outsideAreas, -1, true)]
        : []),
    ];

    return data.sort((a, b) => {
      if (sortOption === "highest_order_count") {
        return b.orderCount - a.orderCount;
      }

      if (sortOption === "area_name") {
        return a.areaName.localeCompare(b.areaName, i18n.language);
      }

      const aAverage = getNullableNumber(a.averageDeliveryMinutes);
      const bAverage = getNullableNumber(b.averageDeliveryMinutes);

      if (aAverage === null && bAverage === null) return 0;
      if (aAverage === null) return 1;
      if (bAverage === null) return -1;

      return sortOption === "lowest_average"
        ? aAverage - bAverage
        : bAverage - aAverage;
    });
  }, [i18n.language, sortOption, stats]);

  if (!stats) return null;

  const disabledFutureDate = (date: Dayjs) =>
    date && date.isAfter(dayjs(), "day");

  const formatNumber = (value: number | null | undefined) => {
    const number = getNullableNumber(value);
    return number === null ? "-" : numberFormatter.format(number);
  };

  const formatMinutes = (value: number | null | undefined) => {
    const number = getNullableNumber(value);
    return number === null
      ? "-"
      : `${numberFormatter.format(number)} ${t("minutes")}`;
  };

  const validateDates = () => {
    const errors: FieldErrors = {};
    const today = dayjs();
    const start = startDate ? dayjs(startDate, DATE_FORMAT, true) : null;
    const end = endDate ? dayjs(endDate, DATE_FORMAT, true) : null;

    if (startDate && (!start?.isValid() || start.isAfter(today, "day"))) {
      errors.startDate = t("invalid_date");
    }

    if (endDate && (!end?.isValid() || end.isAfter(today, "day"))) {
      errors.endDate = t("invalid_date");
    }

    if (start?.isValid() && end?.isValid() && end.isBefore(start, "day")) {
      errors.endDate = t("end_date_before_start_date");
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const refreshDeliveryTimeStats = async () => {
    if (!validateDates()) return;

    try {
      setLoading(true);
      setFieldErrors({});

      const { data } = await EndPoints.statistics.getAreaDeliveryTimeStats({
        startDate,
        endDate,
      });

      onChange(data);
      setStartDate(data.startDate);
      setEndDate(data.endDate);
    } catch (error) {
      setFieldErrors({
        startDate: extractFieldError(error, "startDate"),
        endDate: extractFieldError(error, "endDate"),
      });
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<IAreaDeliveryTimeRow> = [
    {
      title: t("area"),
      dataIndex: "areaName",
      key: "areaName",
      ellipsis: true,
      render: (name: string, row) => (
        <div className={styles.areaName}>
          <span>{row.isOutside ? t("outside_areas") : name}</span>
          {row.isOutside && <Tag color="blue">{t("outside_areas")}</Tag>}
        </div>
      ),
    },
    {
      title: t("orders"),
      dataIndex: "orderCount",
      key: "orderCount",
      width: 110,
      sorter: (a, b) => a.orderCount - b.orderCount,
      render: (value: number) => (
        <span className={styles.count}>{numberFormatter.format(value)}</span>
      ),
    },
    {
      title: t("average"),
      dataIndex: "averageDeliveryMinutes",
      key: "averageDeliveryMinutes",
      width: 130,
      sorter: (a, b) =>
        (a.averageDeliveryMinutes ?? -1) - (b.averageDeliveryMinutes ?? -1),
      render: formatMinutes,
    },
    {
      title: t("median"),
      dataIndex: "medianDeliveryMinutes",
      key: "medianDeliveryMinutes",
      width: 130,
      sorter: (a, b) =>
        (a.medianDeliveryMinutes ?? -1) - (b.medianDeliveryMinutes ?? -1),
      render: formatMinutes,
    },
    {
      title: t("min_label"),
      dataIndex: "minDeliveryMinutes",
      key: "minDeliveryMinutes",
      width: 110,
      render: formatMinutes,
    },
    {
      title: t("max_label"),
      dataIndex: "maxDeliveryMinutes",
      key: "maxDeliveryMinutes",
      width: 110,
      render: formatMinutes,
    },
  ];

  return (
    <div className={styles.container}>
      <Divider orientation="center">{t("area_delivery_time_stats")}</Divider>
      <Card className={styles.panel} bodyStyle={{ padding: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} lg={8}>
            <Statistic
              title={
                <Space>
                  <ClockCircleOutlined />
                  <span>{t("average_delivery_time")}</span>
                </Space>
              }
              value={formatNumber(stats.averageDeliveryMinutes)}
              suffix={
                getNullableNumber(stats.averageDeliveryMinutes) === null
                  ? undefined
                  : t("minutes")
              }
            />
            <Typography.Text type="secondary">
              {numberFormatter.format(stats.includedOrderCount)}{" "}
              {t("completed_orders")}
            </Typography.Text>
            <div className={styles.period}>
              {stats.startDate} - {stats.endDate}
              {stats.excludedOrderCount > 0 && (
                <Tooltip
                  title={t("excluded_delivery_time_orders_tooltip", {
                    count: stats.excludedOrderCount,
                  })}
                >
                  <Tag color="warning" icon={<WarningOutlined />}>
                    {numberFormatter.format(stats.excludedOrderCount)}
                  </Tag>
                </Tooltip>
              )}
            </div>
          </Col>
          <Col xs={24} lg={16}>
            <Row gutter={[12, 8]} align="top">
              <Col xs={24} sm={8}>
                <Form.Item
                  label={t("startDate")}
                  validateStatus={fieldErrors.startDate ? "error" : undefined}
                  help={fieldErrors.startDate}
                >
                  <DatePicker
                    allowClear
                    disabledDate={disabledFutureDate}
                    format={DATE_FORMAT}
                    value={startDate ? dayjs(startDate) : undefined}
                    onChange={(_, value) =>
                      setStartDate(normalizeDatePickerValue(value))
                    }
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  label={t("endDate")}
                  validateStatus={fieldErrors.endDate ? "error" : undefined}
                  help={fieldErrors.endDate}
                >
                  <DatePicker
                    allowClear
                    disabledDate={disabledFutureDate}
                    format={DATE_FORMAT}
                    value={endDate ? dayjs(endDate) : undefined}
                    onChange={(_, value) =>
                      setEndDate(normalizeDatePickerValue(value))
                    }
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item label={t("sort_by")}>
                  <Select
                    value={sortOption}
                    onChange={setSortOption}
                    options={[
                      {
                        value: "highest_average",
                        label: t("highest_average_first"),
                      },
                      {
                        value: "lowest_average",
                        label: t("lowest_average_first"),
                      },
                      {
                        value: "highest_order_count",
                        label: t("highest_order_count_first"),
                      },
                      { value: "area_name", label: t("area_name") },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              loading={loading}
              onClick={refreshDeliveryTimeStats}
            >
              {t("refresh")}
            </Button>
          </Col>
        </Row>
        <div className={styles.tablePanel}>
          <Table
            rowKey="key"
            columns={columns}
            dataSource={rows}
            pagination={false}
            rowClassName={(row) => (row.isOutside ? styles.outsideRow : "")}
            scroll={{ x: 860 }}
          />
        </div>
      </Card>
    </div>
  );
};

export default AreaDeliveryTimeStats;
